import { gql } from "@apollo/client";
import { BASKET_UPDATE_FRAGMENT } from "lib/apollo/fragments";
import { wasMembershipAddedToBasket } from "lib/apollo/variables";
import { createRequestContext, getClient } from "lib/apolloClient";
import { setBasket } from "lib/basket";
import { getMembershipVariant } from "lib/memberships";
import { setNotifications } from "lib/notifications";
import { setTermsAndConditions } from "lib/termsAndConditions";
import { setUser } from "lib/user";
import { MEMBERSHIP_DETAILS_FRAGMENT } from "packages/basket-and-checkout/shared/components/ATBConfirmation/MembershipDetails/MembershipDetailsWrapper";
import type {
  BasketMutationFlagsInput,
  BasketUpdateResultType,
  ItemInput,
  ItemOriginEnum,
  ItemTypeEnum
} from "@shared/graphql.types";
import { removeNull } from "utils/removeNull";
import { v4 as uuid } from "uuid";
import { withGlobalSpinner } from "./api";
import { handleProductAddedUpdateTracking } from "./utils/basketUpdateTracking";

export type AddBasketItemType = Omit<ItemInput, "id">;

const ADD_BASKET_ITEMS = gql`
  mutation addBasketItems(
    $listId: ID!
    $version: Int!
    $items: [ItemInput!]!
    $basketMutationFlags: BasketMutationFlagsInput
  ) {
    addBasketItems(
      listId: $listId
      version: $version
      items: $items
      basketMutationFlags: $basketMutationFlags
    ) {
      ...BasketUpdateFragment
      membershipDetails {
        ...MembershipDetailsFragment
      }
    }
  }
  ${BASKET_UPDATE_FRAGMENT}
  ${MEMBERSHIP_DETAILS_FRAGMENT}
`;

const baseAddToBasket = async (
  items: Omit<ItemInput, "id">[],
  basketMutationFlags: BasketMutationFlagsInput = {}
) => {
  const variables = {
    listId: "",
    version: -1,
    items: items.map((item) => ({
      ...item,
      id: uuid(),
      maxItemQuantity: item.maxItemQuantity
    })),
    basketMutationFlags
  };

  const result = await getClient().mutate<{
    addBasketItems: BasketUpdateResultType;
  }>({
    mutation: ADD_BASKET_ITEMS,
    variables,
    context: createRequestContext({
      sessionStrategy: "START_NEW_ANONYMOUS",
      waitForFingerPrint: true
    })
  });

  if (!result?.data?.addBasketItems) throw new Error("No response received");

  const {
    items: basket,
    itemUpdates,
    discounts,
    pricing,
    includedDiscounts,
    rejectedDiscounts,
    notifications,
    hasOnlyVirtualProducts,
    deliveryMethod,
    deliveryMethodName,
    termsAndConditions,
    gwpModule,
    thresholds,
    spotlightModule,
    listId
  } = result.data.addBasketItems;

  wasMembershipAddedToBasket(
    itemUpdates.some((update) => update.itemType === "membership")
  );

  setUser({
    basket,
    basketId: listId,
    discounts,
    pricing,
    includedDiscounts,
    rejectedDiscounts,
    hasOnlyVirtualProducts: !!hasOnlyVirtualProducts,
    deliveryMethod,
    deliveryMethodName: removeNull(deliveryMethodName),
    spotlightModule
  });

  setBasket({ gwpModule, thresholds, items: basket });

  setTermsAndConditions(termsAndConditions);

  setNotifications(notifications);

  return result.data.addBasketItems;
};

const addBasketItemsWithTracking = async (
  items: Omit<ItemInput, "id">[],
  locationType = "Page",
  locationTypeDetails?: string,
  basketMutationFlags: BasketMutationFlagsInput = {}
) => {
  const result = await baseAddToBasket(items, basketMutationFlags);

  handleProductAddedUpdateTracking({
    trackingEvents: result.updateTracking,
    basketItems: result.items,
    itemUpdates: result.itemUpdates,
    locationType,
    locationTypeDetails
  });

  return result;
};

export type BasketItem = Omit<ItemInput, "id">;

export const addBasketItemsWithSpinner = withGlobalSpinner(
  (
    items: AddBasketItemType[],
    locationType?: string,
    locationTypeDetails?: string,
    basketMutationFlags: BasketMutationFlagsInput = {}
  ) =>
    addBasketItemsWithTracking(
      items,
      locationType,
      locationTypeDetails,
      basketMutationFlags
    )
);

export const addBasketItems = (
  items: AddBasketItemType[],
  locationType?: string,
  locationTypeDetails?: string,
  basketMutationFlags: BasketMutationFlagsInput = {}
): Promise<BasketUpdateResultType> =>
  addBasketItemsWithTracking(
    items,
    locationType,
    locationTypeDetails,
    basketMutationFlags
  );

export const getMembershipWithId = (id: string): ItemInput => {
  const membershipId = getMembershipVariant(id);
  return {
    id: uuid(),
    variantId: null,
    variantSku: null,
    membershipId,
    quantity: 1,
    type: "membership" as ItemTypeEnum,
    origin: "user" as ItemOriginEnum
  };
};

export const addMembershipById = (
  membershipId: string,
  options?: { runInBackground?: boolean },
  locationType?: string,
  locationTypeDetails?: string,
  basketMutationFlags = { explicitUserMembershipAdd: true }
): Promise<BasketUpdateResultType> => {
  if (options?.runInBackground) {
    return addBasketItems(
      [getMembershipWithId(membershipId)],
      locationType,
      locationTypeDetails,
      basketMutationFlags
    );
  }

  return addBasketItemsWithSpinner(
    [getMembershipWithId(membershipId)],
    locationType,
    locationTypeDetails,
    basketMutationFlags
  );
};
